import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import consumer from '@/channels/consumer'
import { UploadFile } from './useTusUpload'
import { UploadFileStatus } from '@components/feedback/Toast'

interface UseBulkUploadNotificationProps {
  files: UploadFile[]
  folderId?: string | null
  showToast: (
    type: 'info' | 'success' | 'error' | 'warning' | 'loading',
    title: string,
    options?: {
      message?: string
      duration?: number
      dismissible?: boolean
      progress?: number
      files?: UploadFileStatus[]
    }
  ) => string
  updateToast: (id: string, updates: any) => void
  dismissToast: (id: string) => void
}

export function useBulkUploadNotification({
  files,
  folderId: defaultFolderId,
  showToast,
  updateToast,
  dismissToast
}: UseBulkUploadNotificationProps) {
  const { t } = useTranslation()
  const toastIdRef = useRef<string | null>(null)
  const subscriptionRef = useRef<any>(null)
  const isProcessingRef = useRef(false)
  const isTrackingRef = useRef(false)
  const lastUpdateRef = useRef<string>('')
  const folderIdRef = useRef<string | null | undefined>(defaultFolderId)
  const [trackingTrigger, setTrackingTrigger] = useState(0)

  // Start tracking uploads
  const startTracking = useCallback((overrideFolderId?: string | null) => {
    if (files.length === 0) return

    // Store the folder ID to use for submission
    folderIdRef.current = overrideFolderId !== undefined ? overrideFolderId : defaultFolderId

    // Reset all refs for new upload session
    isTrackingRef.current = true
    isProcessingRef.current = false
    lastUpdateRef.current = '' // Reset to force effect to re-evaluate

    // Cleanup any existing subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
      subscriptionRef.current = null
    }

    // Create initial notification
    const toastId = showToast('loading', t('fileExplorer.bulkUpload.uploading', { count: files.length }), {
      dismissible: false,
      progress: 0
    })
    toastIdRef.current = toastId

    // Trigger the effect to re-evaluate (in case files are already uploaded)
    setTrackingTrigger(prev => prev + 1)
  }, [files, showToast, defaultFolderId, t])

  // Update upload progress
  useEffect(() => {
    if (!toastIdRef.current || files.length === 0 || !isTrackingRef.current || isProcessingRef.current) return

    // Create state signature with rounded progress (to nearest 5% to reduce updates)
    const stateSignature = files.map(f =>
      `${f.id}:${f.status}:${Math.round(f.progress / 5) * 5}`
    ).join('|')

    // Skip if nothing changed
    if (stateSignature === lastUpdateRef.current) return
    lastUpdateRef.current = stateSignature

    // Calculate stats
    const uploadingCount = files.filter(f => f.status === 'uploading').length
    const successCount = files.filter(f => f.status === 'success').length
    const totalProgress = files.reduce((sum, f) => sum + f.progress, 0) / files.length

    // Update notification
    updateToast(toastIdRef.current, {
      title: uploadingCount > 0
        ? t('fileExplorer.bulkUpload.uploadingProgress', { current: uploadingCount, total: files.length })
        : t('fileExplorer.bulkUpload.uploadComplete', { success: successCount, total: files.length }),
      progress: totalProgress
    })

    // Check if all complete
    const allComplete = files.every(f => f.status === 'success' || f.status === 'error')
    if (allComplete && successCount > 0) {
      isProcessingRef.current = true
      submitToBackend()
    } else if (allComplete && successCount === 0) {
      isProcessingRef.current = true
      updateToast(toastIdRef.current, {
        type: 'error',
        title: t('fileExplorer.bulkUpload.uploadFailed'),
        message: t('fileExplorer.bulkUpload.allFailed', { count: files.length }),
        dismissible: true,
        progress: undefined
      })
      setTimeout(() => {
        if (toastIdRef.current) dismissToast(toastIdRef.current)
      }, 5000)
    }
  }, [files, updateToast, dismissToast, trackingTrigger, t])

  // Submit successful uploads to backend
  const submitToBackend = async () => {
    if (!toastIdRef.current) return

    const successfulFiles = files.filter(f => f.status === 'success' && f.uploadedData)

    if (successfulFiles.length === 0) return

    // Update toast to show processing state
    updateToast(toastIdRef.current, {
      title: t('fileExplorer.bulkUpload.processing', { count: successfulFiles.length }),
      message: t('fileExplorer.bulkUpload.creatingDocuments'),
      progress: 0
    })

    try {
      // Prepare file data for backend
      const filesData = successfulFiles.map(f => JSON.parse(f.uploadedData!))
      const fileNames = successfulFiles.reduce((acc, file, index) => {
        acc[index.toString()] = file.name
        return acc
      }, {} as Record<string, string>)

      // Generate a temporary job_id to subscribe before making the request
      // This prevents race condition where job completes before subscription is ready
      const tempJobId = crypto.randomUUID()

      // Subscribe FIRST and wait for confirmation
      await subscribeToProgress(tempJobId)

      // Call bulk_create endpoint with our job_id
      const response = await fetch('/bulk_create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          job_id: tempJobId,
          files: filesData,
          file_names: fileNames,
          folder_id: folderIdRef.current || undefined
        })
      })

      if (!response.ok) {
        throw new Error(t('fileExplorer.bulkUpload.failedToStart'))
      }
    } catch (error) {
      console.error('[BulkUpload] Failed to submit bulk upload:', error)
      updateToast(toastIdRef.current, {
        type: 'error',
        title: t('fileExplorer.bulkUpload.failedToProcess'),
        message: error instanceof Error ? error.message : t('fileExplorer.bulkUpload.unknownError'),
        dismissible: true,
        progress: undefined
      })
    }
  }

  // Subscribe to ActionCable for backend progress
  const subscribeToProgress = useCallback((jobId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!toastIdRef.current) {
        reject(new Error('No toast ID'))
        return
      }

      subscriptionRef.current = consumer.subscriptions.create(
        { channel: 'BulkUploadChannel', job_id: jobId },
        {
          connected: () => {
            resolve()
          },
          disconnected: () => {
            // Connection closed
          },
          rejected: () => {
            reject(new Error('Subscription rejected'))
          },
          received: (data: any) => {
          if (!toastIdRef.current) return

          if (data.type === 'progress') {
            const progress = (data.completed / data.total) * 100
            updateToast(toastIdRef.current, {
              title: t('fileExplorer.bulkUpload.processingProgress', { current: data.completed, total: data.total }),
              progress: progress
            })
          } else if (data.type === 'complete') {
            const { summary } = data

            if (summary.failed === 0) {
              // All successful
              updateToast(toastIdRef.current, {
                type: 'success',
                title: t('fileExplorer.bulkUpload.success'),
                message: t('fileExplorer.bulkUpload.successMessage', { count: summary.success }),
                dismissible: true,
                progress: undefined
              })

              // Reload the file explorer to show new files
              router.reload({ except: ['breadcrumbs', 'folder_tree', 'storage_usage'] })
            } else {
              // Some failed
              updateToast(toastIdRef.current, {
                type: 'warning',
                title: t('fileExplorer.bulkUpload.completedWithErrors'),
                message: t('fileExplorer.bulkUpload.partialSuccess', { success: summary.success, failed: summary.failed }),
                dismissible: true,
                progress: undefined
              })
            }

            // Auto-dismiss after 5 seconds
            setTimeout(() => {
              if (toastIdRef.current) dismissToast(toastIdRef.current)
            }, 5000)

            // Cleanup subscription and reset state
            if (subscriptionRef.current) {
              subscriptionRef.current.unsubscribe()
              subscriptionRef.current = null
            }

            // Reset refs for next upload
            isTrackingRef.current = false
            isProcessingRef.current = false
            lastUpdateRef.current = ''
          }
        }
      }
    )
    })
  }, [t, updateToast, dismissToast])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
      }
    }
  }, [])

  return {
    startTracking
  }
}
